import { BaseObjectStrapi, BaseResponseStrapi, StrapiApiError } from 'types/strapiApi';

type RequestParams = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  data?: BaseObjectStrapi;
  [key: string]: any;
};

export const requestStrapi = <
  D extends BaseObjectStrapi | Array<BaseObjectStrapi> = BaseObjectStrapi,
  M extends BaseObjectStrapi = BaseObjectStrapi,
  E = StrapiApiError,
>() => {
  let abortController: AbortController | null = null;

  const request = async (params: RequestParams): Promise<BaseResponseStrapi<D, M, E>> => {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    const { url, method = 'GET', headers = {}, data, ...rest } = params;
    const body = data ? JSON.stringify(data) : undefined;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body,
        signal: abortController.signal,
        ...rest,
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          isError: true,
          error: { status: response.status, message: response.statusText } as E,
          statusCode: response.status,
        };
      }

      return {
        isError: false,
        data: json.data ? (json.data as D) : (json as D),
        meta: json.meta as M,
      };
    } catch (e: any) {
      if (e.name === 'AbortError') {
        return {
          isError: true,
          error: { status: 0, message: 'Request aborted' } as E,
        };
      }

      return {
        isError: true,
        error: { status: 0, message: e.message ?? 'Unknown error' } as E,
      };
    } finally {
      abortController = null;
    }
  };

  const cancel = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return { request, cancel };
};
